import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const { t, language, setLanguage, currentLanguage, supportedLanguages } = useLanguage();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsLangOpen(false);
  };

  const navItems = [
    { label: t('header.home'), target: 'inicio' },
    { label: t('header.features'), target: 'caracteristicas' },
    { label: t('header.screenshots'), target: 'capturas' },
    { label: 'FARM MCP', target: 'mcp' },
  ];

  const announcement = t('hero.badge').replace('🚀 ', '');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/[0.86] text-[#fffefa] backdrop-blur-xl">
      <div className="border-b border-white/10 bg-[#0b0b0f]">
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-center px-4 text-center text-xs text-[#c9c8d0] sm:px-6 lg:px-8">
          <span className="mr-2 h-1.5 w-1.5 rounded-full bg-[#0a84ae] shadow-[0_0_18px_rgba(10,132,174,0.85)]" />
          <span>{announcement}</span>
          <button
            onClick={() => scrollToSection('descargar')}
            className="ml-3 underline decoration-[#0a84ae] underline-offset-4 transition-colors duration-200 hover:text-white"
          >
            {t('header.downloadApp')}
          </button>
        </div>
        <div className="h-px bg-gradient-to-r from-[#0a84ae] via-[#7f49f5] to-[#0a84ae]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <button
            onClick={() => scrollToSection('inicio')}
            className="flex items-center gap-3 text-left"
            aria-label="FARM"
          >
            <img
              src="/farm-logo.png"
              alt="FARM"
              className="h-9 w-9 rounded-md shadow-[0_0_28px_rgba(127,73,245,0.38)]"
            />
            <div className="leading-none">
              <div className="text-base font-medium text-white">FARM</div>
              <div className="mt-1 text-[11px] font-medium uppercase text-[#8d8d95]">by Farmway</div>
            </div>
          </button>

          <nav className="hidden items-center gap-7 md:flex">
            {navItems.map((item) => (
              <button
                key={item.target}
                onClick={() => scrollToSection(item.target)}
                className="text-sm font-normal text-[#d7d6de] transition-colors duration-200 hover:text-white"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <div className="relative" ref={langDropdownRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex min-h-[40px] items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-3 text-sm font-medium text-[#fffefa] transition-colors duration-200 hover:bg-white/[0.08]"
                aria-label={t('language.select')}
              >
                <span>{currentLanguage.code.toUpperCase()}</span>
                <svg
                  className={`h-4 w-4 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLangOpen && (
                <div className="absolute right-0 mt-3 w-56 rounded-xl border border-white/10 bg-[#15151b] p-2 shadow-2xl shadow-black/50">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors duration-150 ${language === lang.code
                        ? 'bg-white text-black'
                        : 'text-[#d7d6de] hover:bg-white/[0.07] hover:text-white'
                        }`}
                    >
                      <span>{lang.name}</span>
                      <span className="text-xs font-medium uppercase">{lang.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('descargar')}
              className="min-h-[40px] rounded-md bg-white px-5 text-sm font-medium text-black shadow-[inset_0_-12px_20px_rgba(255,255,255,0.45),0_4px_14px_rgba(255,255,255,0.14)] transition-transform duration-200 hover:-translate-y-0.5"
            >
              {t('header.downloadApp')}
            </button>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="rounded-md border border-white/10 p-2 text-white md:hidden"
            aria-label="Menu"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-white/10 py-5 md:hidden">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.target}
                  onClick={() => scrollToSection(item.target)}
                  className="rounded-md px-3 py-3 text-left text-sm text-[#d7d6de] hover:bg-white/[0.06] hover:text-white"
                >
                  {item.label}
                </button>
              ))}

              <div className="mt-3 border-t border-white/10 pt-4">
                <p className="mb-2 px-3 text-xs font-medium uppercase text-[#8d8d95]">{t('language.select')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        setIsMenuOpen(false);
                      }}
                      className={`rounded-md px-3 py-2 text-sm font-medium uppercase transition-colors duration-150 ${language === lang.code
                        ? 'bg-white text-black'
                        : 'bg-white/[0.05] text-[#d7d6de] hover:bg-white/[0.1] hover:text-white'
                        }`}
                    >
                      {lang.code}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => scrollToSection('descargar')}
                className="mt-4 min-h-[48px] rounded-md bg-white px-6 text-sm font-medium text-black shadow-[inset_0_-12px_20px_rgba(255,255,255,0.45),0_4px_14px_rgba(255,255,255,0.14)]"
              >
                {t('header.downloadApp')}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
